/**
 * Per-column expression editor for a `formula` column. Committed only on Save;
 * re-seeded each open from the persisted expression.
 *
 * The available column names are listed rather than left to memory: a
 * reference is matched by label, so a formula silently returns nothing when the
 * name is off by a character.
 */
import { Input, Label } from '@monksflow/monks-ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormDialog } from '@/shared/ui/FormDialog';

import { FORMULA_REFERENCEABLE_TYPES } from '../../lib/evaluateFormula';
import type { CustomColumn } from '../../lib/useTrackersBoard';

interface FormulaConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Current expression for the column being configured. */
  expression: string;
  /** The other columns on the same board; the numeric ones are offered. */
  columns: CustomColumn[];
  onSave: (next: string) => void;
}

export function FormulaConfigModal({
  open,
  onOpenChange,
  expression,
  columns,
  onSave,
}: FormulaConfigModalProps) {
  const { t } = useTranslation('trackers');

  /**
   * Only columns a formula can read a number out of. Offering the rest would
   * hand someone a chip that inserts a reference which can never resolve, and
   * the formula then renders nothing with no hint as to why.
   */
  const referenceable = columns.filter((column) => FORMULA_REFERENCEABLE_TYPES.has(column.type));

  const [draft, setDraft] = useState(expression);
  useEffect(() => {
    if (open) setDraft(expression);
  }, [open, expression]);

  const handleSave = () => {
    onSave(draft.trim());
    onOpenChange(false);
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('trackersBoard.formulaConfigModal.configureFormula')}
      description={t('trackersBoard.formulaConfigModal.referenceOtherColumnsBy')}
    >
      <FormDialog.Body>
        <div className="flex flex-col gap-1">
          <Label htmlFor="formula-expression-input" className="text-xs text-muted-foreground">
            {t('trackersBoard.formulaConfigModal.expression')}
          </Label>
          <Input
            id="formula-expression-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t('trackersBoard.formulaConfigModal.eGBudgetHours')}
            className="font-mono"
          />
        </div>
        {referenceable.length > 0 ? (
          <div className="flex flex-col gap-1">
            <p className="text-xs text-muted-foreground">
              {t('trackersBoard.formulaConfigModal.availableColumns')}
            </p>
            <div className="flex flex-wrap gap-1">
              {referenceable.map((column) => (
                <button
                  key={column.id}
                  type="button"
                  onClick={() => setDraft((prev) => `${prev}{${column.label}}`)}
                  className="rounded-full bg-muted px-2 py-0.5 font-mono text-xs transition-colors hover:bg-accent"
                >
                  {column.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </FormDialog.Body>
      <FormDialog.Footer
        secondary={{
          label: t('trackersBoard.formulaConfigModal.cancel'),
          onClick: () => onOpenChange(false),
        }}
        primary={{ label: t('trackersBoard.formulaConfigModal.save'), onClick: handleSave }}
      />
    </FormDialog>
  );
}
